import { useCallback, useEffect, useState } from 'react';
import aiAutomationApi from './aiAutomationApi';

export const COMPANY_FEATURES = {
  resume_upload: 'Resume Upload',
  sourcing: 'Sourcing',
  job_posting: 'Job Posting',
  outreach: 'Outreach',
  email_sync: 'Mail Sync Based on Subject',
  applicant_sync: 'Applicant Sync',
  import_candidate: 'Import Candidate',
  scheduling: 'Scheduling',
  manual_interview: 'Manual Interview',
  ai_interview: 'AI Interview',
  filter: 'Filter',
};

export const DEFAULT_COMPANY_FEATURE_ACCESS = Object.keys(COMPANY_FEATURES).reduce((acc, key) => {
  acc[key] = true;
  return acc;
}, {});

export const normalizeCompanyFeatureAccess = (features) => ({
  ...DEFAULT_COMPANY_FEATURE_ACCESS,
  ...(features && typeof features === 'object' ? features : {}),
});

export const canUseCompanyFeature = (features, feature) => {
  if (!feature) return true;
  if (features == null) return false;
  const normalized = normalizeCompanyFeatureAccess(features);
  return normalized[feature] !== false;
};

export const getFeatureBlockedMessage = (feature) => {
  const label = COMPANY_FEATURES[feature] || String(feature || 'This feature');
  return `${label} is not enabled for your company. Please contact your admin.`;
};

export const fetchMyCompanyFeatureAccess = async () => {
  const response = await aiAutomationApi.get('/company-feature-access/me');
  return normalizeCompanyFeatureAccess(response?.features);
};

export const fetchFeatureAccessCompanies = async ({ search = '', feature = '', enabled = '' } = {}) => {
  const params = new URLSearchParams();
  if (search) params.set('search', search);
  if (feature) params.set('feature', feature);
  if (enabled !== '') params.set('enabled', String(enabled));
  const query = params.toString();
  const response = await aiAutomationApi.get(`/company-feature-access/companies/list${query ? `?${query}` : ''}`);
  return Array.isArray(response?.companies) ? response.companies : [];
};

export const saveCompanyFeatureAccess = async (companyId, features) => {
  const response = await aiAutomationApi.put('/company-feature-access', {
    company_id: Number(companyId),
    features: normalizeCompanyFeatureAccess(features),
  });
  return normalizeCompanyFeatureAccess(response?.features);
};

export const useCompanyFeatureAccess = () => {
  const [features, setFeatures] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const nextFeatures = await fetchMyCompanyFeatureAccess();
      setFeatures(nextFeatures);
      return nextFeatures;
    } catch (error) {
      console.warn('Failed to load company feature access; using full access fallback.', error);
      setFeatures(DEFAULT_COMPANY_FEATURE_ACCESS);
      return DEFAULT_COMPANY_FEATURE_ACCESS;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const canUseFeature = useCallback((feature) => canUseCompanyFeature(features, feature), [features]);

  return {
    features,
    loading,
    canUseFeature,
    refresh,
  };
};
